import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    InputAdornment,
    IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import * as authService from '../../services/authService';
import { APP_NAME } from '../../config/constants';

// Styled Components
const BackgroundWrapper = styled(Box)({
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `
        linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)),
        url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M36 34c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm-8-16c2.761 0 5 2.239 5 5s-2.239 5-5 5-5-2.239-5-5 2.239-5 5-5z' stroke='%2384c484' stroke-width='2'/%3E%3Cpath d='M24 44c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm12-24c2.761 0 5 2.239 5 5s-2.239 5-5 5-5-2.239-5-5 2.239-5 5-5z' stroke='%2395d195' stroke-width='2'/%3E%3C/g%3E%3C/svg%3E")
    `,
    backgroundRepeat: 'repeat',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(144, 238, 144, 0.1) 0%, rgba(0, 128, 0, 0.1) 100%)',
        zIndex: 0
    }
});

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    width: '60%',
    maxWidth: '300px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    backgroundColor: '#4CAF50',
    borderRadius: '50%',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '&:hover fieldset': {
            borderColor: '#4CAF50',
        },
        '& fieldset': {
            borderColor: '#81C784',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '12px 14px',
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: '10px',
    padding: '8px',
    fontSize: '1rem',
    textTransform: 'none',
    background: `linear-gradient(45deg, #4CAF50 30%, #81C784 90%)`,
    boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
    '&:hover': {
        background: `linear-gradient(45deg, #388E3C 30%, #4CAF50 90%)`,
    },
}));

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');

        if (validateForm()) {
            try {
                await authService.login(
                    formData.username,
                    formData.password
                );
                navigate('/dashboard');
            } catch (error) {
                setApiError(error.message || 'Login failed. Please try again.');
            }
        }
    };

    return (
        <BackgroundWrapper>
            <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <StyledPaper>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <LogoContainer>
                                <LockOutlinedIcon sx={{ color: 'white', fontSize: 24 }} />
                            </LogoContainer>
                            <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#2E7D32' }}>
                                {APP_NAME} - Welcome
                            </Typography>
                        </Box>

                        {apiError && (
                            <Alert severity="error" sx={{ mt: 1, borderRadius: '10px' }}>
                                {apiError}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                            <StyledTextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="Username"
                                name="username"
                                autoComplete="username"
                                autoFocus
                                value={formData.username}
                                onChange={handleChange}
                                error={!!errors.username}
                                helperText={errors.username}
                                sx={{ mb: 1 }}
                                size="small"
                            />
                            <StyledTextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={formData.password}
                                onChange={handleChange}
                                error={!!errors.password}
                                helperText={errors.password}
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                                size="small"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <StyledButton
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 1 }}
                            >
                                Sign In
                            </StyledButton>
                        </Box>
                    </StyledPaper>
                </Box>
            </Container>
        </BackgroundWrapper>
    );
};

export default Login;
